import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getElogiAgente } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ScrollText, Calendar, Clock, Trash2 } from 'lucide-react';

const AgenteElogiPage = () => {
  const { idAgente } = useParams();
  const [elogi, setElogi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agente, setAgente] = useState(null);

  useEffect(() => {
    loadElogi();
  }, [idAgente]);

  const loadElogi = async () => {
    try {
      const response = await getElogiAgente(idAgente);
      setElogi(response.data.elogi || []);
      setAgente(response.data.agente || null);
    } catch (error) {
      console.error('Errore caricamento elogi:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getDaysRemaining = (createdAt) => {
    if (!createdAt) return null;
    const created = new Date(createdAt);
    const expiry = new Date(created.getTime() + 3 * 24 * 60 * 60 * 1000); // +3 giorni
    const now = new Date();
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="agente-elogi-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/agenti">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna agli Agenti
          </Button>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Elogi Demo
          </h1>
          {agente && (
            <p className="text-sm text-slate-500 mt-1">
              Agente: {agente.nome} {agente.cognome}
            </p>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-800">Elogi Demo Temporanei</p>
              <p className="text-sm text-purple-600">
                Gli elogi demo vengono eliminati automaticamente dopo 3 giorni dalla creazione.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Elogi */}
      {elogi.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <ScrollText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun elogio demo creato da questo agente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {elogi.map((elogio) => {
            const daysRemaining = getDaysRemaining(elogio.created_at);
            return (
              <Card key={elogio.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <ScrollText className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {elogio.nome} {elogio.cognome}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(elogio.data_nascita)} - {formatDate(elogio.data_morte)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Creato: {formatDate(elogio.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-purple-100 text-purple-700">DEMO</Badge>
                      {daysRemaining !== null && (
                        <Badge 
                          variant="outline" 
                          className={daysRemaining <= 1 ? 'text-red-600 border-red-300' : 'text-orange-600 border-orange-300'}
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          {daysRemaining === 0 ? 'Scade oggi' : `${daysRemaining} giorni rimasti`}
                        </Badge>
                      )}
                      <Link to={`/elogi/${elogio.id}`}>
                        <Button variant="outline" size="sm">
                          Visualizza
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgenteElogiPage;
